import React, { useState, useEffect } from 'react';
import { Button, Badge, Input, Select, Card, Loader } from './shared';

const ErrorMonitor = ({ service, isOnline }) => {
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingProjects, setLoadingProjects] = useState(false);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const PROJECT_API_BASE = window.location.protocol === 'https:' ? '/api/projects' : `http://${window.location.hostname}:8010`;

    useEffect(() => {
        if (isOnline) {
            fetchProjects();
        }
    }, [isOnline]);

    const fetchProjects = async () => {
        setLoadingProjects(true);
        try {
            const response = await fetch(`${PROJECT_API_BASE}/projects`);
            if (!response.ok) throw new Error('Failed to fetch projects');
            const data = await response.json();

            // Filter projects that have Sentry project IDs
            const projectsWithSentry = data.filter(p => p.sentry_project_ids && p.sentry_project_ids.length > 0);
            setProjects(projectsWithSentry);

            // Auto-select first project if available
            if (projectsWithSentry.length > 0) {
                setSelectedProject(projectsWithSentry[0]);
                fetchErrors(projectsWithSentry[0].sentry_project_ids);
            }
        } catch (err) {
            console.error('Error fetching projects:', err);
            setError(err.message);
        } finally {
            setLoadingProjects(false);
        }
    };

    const fetchErrors = async (sentryProjectIds) => {
        if (!sentryProjectIds || sentryProjectIds.length === 0) {
            setError('No Sentry project IDs configured for this project');
            return;
        }

        setLoading(true);
        setError(null);
        setErrors([]);

        try {
            // Fetch errors from all Sentry projects and aggregate them
            const allErrors = [];

            for (const projectId of sentryProjectIds) {
                let url = `${service.url}/errors?project_id=${projectId}&limit=50`;
                if (searchQuery) {
                    url += `&query=${encodeURIComponent(searchQuery)}`;
                }

                const response = await fetch(url, {
                    headers: {
                        'Authorization': `Bearer ${service.apiKey}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    allErrors.push(...(data.errors || []));
                } else if (response.status === 503) {
                    const errorData = await response.json();
                    setError(errorData.detail || 'Sentry is not configured');
                    break;
                } else if (response.status === 404) {
                    console.warn(`Sentry project ${projectId} not found`);
                } else {
                    const errorData = await response.json();
                    console.error(`Error fetching from project ${projectId}:`, errorData);
                }
            }

            // Sort by last seen (most recent first)
            allErrors.sort((a, b) => new Date(b.lastSeen) - new Date(a.lastSeen));
            setErrors(allErrors);

        } catch (err) {
            console.error('Failed to fetch errors:', err);
            setError('Failed to connect to error monitoring service');
        } finally {
            setLoading(false);
        }
    };

    const handleProjectChange = (project) => {
        setSelectedProject(project);
        setSearchQuery('');
        setStatusFilter('');
        if (project.sentry_project_ids && project.sentry_project_ids.length > 0) {
            fetchErrors(project.sentry_project_ids);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (selectedProject?.sentry_project_ids && selectedProject.sentry_project_ids.length > 0) {
            fetchErrors(selectedProject.sentry_project_ids);
        }
    };

    const getErrorLevelColor = (level) => {
        switch (level?.toLowerCase()) {
            case 'error':
                return 'bg-red-100 text-red-800';
            case 'warning':
                return 'bg-yellow-100 text-yellow-800';
            case 'info':
                return 'bg-blue-100 text-blue-800';
            case 'fatal':
                return 'bg-purple-100 text-purple-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'resolved':
                return 'bg-green-100 text-green-800';
            case 'unresolved':
                return 'bg-red-100 text-red-800';
            case 'ignored':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-blue-100 text-blue-800';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const filteredErrors = errors.filter(err => {
        if (statusFilter && err.status !== statusFilter) return false;
        return true;
    });

    if (!isOnline) {
        return (
            <div className="p-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-800">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        Error Monitor service is offline. Please check the service status.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Project Selector */}
            <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">
                    <i className="fas fa-project-diagram mr-2"></i>
                    Select Project
                </h3>

                {loadingProjects ? (
                    <Loader message="Loading projects..." />
                ) : projects.length === 0 ? (
                    <div className="text-center py-8">
                        <i className="fas fa-inbox text-gray-400 text-4xl mb-3"></i>
                        <p className="text-gray-600">No projects with Sentry integration found</p>
                        <p className="text-sm text-gray-500 mt-2">
                            Add a Sentry Project ID to your projects in Project Management to track errors
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {projects.map(project => (
                            <div
                                key={project.id}
                                onClick={() => handleProjectChange(project)}
                                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                    selectedProject?.id === project.id
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-blue-300'
                                }`}
                            >
                                <h4 className="font-semibold text-gray-900">{project.name}</h4>
                                <p className="text-sm text-gray-600 mt-1">
                                    {project.sentry_project_ids.length} Sentry {project.sentry_project_ids.length === 1 ? 'project' : 'projects'} linked
                                </p>
                                {project.domain && (
                                    <p className="text-xs text-blue-600 mt-2">
                                        <i className="fas fa-globe mr-1"></i>
                                        {project.domain}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Search and Filters */}
            {selectedProject && (
                <Card className="p-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                            <Input
                                type="text"
                                placeholder="Search errors (e.g., is:unresolved)"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                fullWidth
                            />
                            <Button type="submit" variant="primary">
                                <i className="fas fa-search mr-2"></i>
                                Search
                            </Button>
                        </form>
                        <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">All Statuses</option>
                            <option value="unresolved">Unresolved</option>
                            <option value="resolved">Resolved</option>
                            <option value="ignored">Ignored</option>
                        </Select>
                        <Button
                            onClick={() => fetchErrors(selectedProject.sentry_project_ids)}
                            variant="secondary"
                        >
                            <i className="fas fa-sync-alt mr-2"></i>
                            Refresh
                        </Button>
                    </div>
                </Card>
            )}

            {/* Error Display */}
            {selectedProject && (
                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold">
                            <i className="fas fa-bug mr-2"></i>
                            Errors for {selectedProject.name}
                        </h3>
                        <Badge variant="primary">{filteredErrors.length} errors</Badge>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                            <p className="text-red-800">
                                <i className="fas fa-exclamation-circle mr-2"></i>
                                {error}
                            </p>
                        </div>
                    )}

                    {loading ? (
                        <Loader message="Loading errors..." />
                    ) : filteredErrors.length === 0 ? (
                        <div className="text-center py-12">
                            <i className="fas fa-check-circle text-green-500 text-4xl mb-3"></i>
                            <p className="text-gray-600">No errors found</p>
                            {searchQuery && (
                                <p className="text-sm text-gray-500 mt-2">
                                    Try adjusting your search query
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filteredErrors.map(err => (
                                <div
                                    key={err.id}
                                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${getErrorLevelColor(err.level)}`}>
                                                    {err.level}
                                                </span>
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(err.status)}`}>
                                                    {err.status}
                                                </span>
                                                <span className="text-xs text-gray-500">{err.shortId}</span>
                                            </div>
                                            <h4 className="font-semibold text-gray-900 mb-1">
                                                {err.title}
                                            </h4>
                                            {err.culprit && (
                                                <p className="text-sm text-gray-600 mb-2">
                                                    <i className="fas fa-code mr-1"></i>
                                                    {err.culprit}
                                                </p>
                                            )}
                                        </div>
                                        <a
                                            href={err.permalink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="ml-4 text-blue-600 hover:text-blue-800"
                                            title="View in Sentry"
                                        >
                                            <i className="fas fa-external-link-alt"></i>
                                        </a>
                                    </div>

                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                        <div>
                                            <span className="text-gray-500">Events:</span>
                                            <span className="ml-2 font-medium">{err.count.toLocaleString()}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">Users:</span>
                                            <span className="ml-2 font-medium">{err.userCount.toLocaleString()}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">First Seen:</span>
                                            <span className="ml-2 font-medium">{formatDate(err.firstSeen)}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">Last Seen:</span>
                                            <span className="ml-2 font-medium">{formatDate(err.lastSeen)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            )}
        </div>
    );
};

export default ErrorMonitor;
